import { Temporal } from 'temporal-polyfill';
import { ICalendarEventMinimumData } from '../../types/ICalendarEvent';
import { ITimeZone } from '../../types/ITimeZone';

export type ICalendarEventExtendedWithUIProps<TEvent> = TEvent & {
	left: string;
	zIndex: number;
	isOverlapped: boolean;
	dayContainMode: 'contain' | 'start' | 'end' | 'overflow';
};

/**
 * This function return the array of events but with 2 new properties.
 * zIndex
 * left
 *
 * This properties can be used by the UI to handle overlaps.
 *
 * O(n^2) with many overlaps
 * O(nlogn) with no overlaps
 */
function assignZIndexAndLeft<TEvent extends ICalendarEventMinimumData>(
	events: Array<TEvent>,
	plainDate: Temporal.PlainDate
): ICalendarEventExtendedWithUIProps<TEvent>[] {
	if (!events || !events.length) {
		return [];
	}

	events.sort((a, b) => Temporal.ZonedDateTime.compare(a.start, b.start));

	const tracks: ICalendarEventExtendedWithUIProps<TEvent>[][] = [];

	events.forEach((event) => {
		let placed = false;
		for (const track of tracks) {
			if (!track.some((e) => eventsOverlap(e, event))) {
				track.push(event as ICalendarEventExtendedWithUIProps<TEvent>);
				placed = true;
				break;
			}
		}
		if (!placed) {
			tracks.push([event as ICalendarEventExtendedWithUIProps<TEvent>]);
		}
	});

	// Asignar zIndex y left basado en el track del evento
	const maxZIndex = tracks.length - 1;
	tracks.forEach((track, index) => {
		track.forEach((event) => {
			event.zIndex = index; // zIndex más alto para los tracks más adelantados
			event.left = `${index * 10}px`; // Desplazamiento a la derecha para cada track
		});
	});

	// If there is more than one column split space evently
	if (maxZIndex > 0) {
		const increment = 100 / (maxZIndex + 1);
		tracks.forEach((track, index) => {
			track.forEach((event) => {
				event.left = `${increment * index}%`;
			});
		});
	}

	// Calcular la propiedad isOverlapped para cada evento
	events.forEach((event) => {
		(event as ICalendarEventExtendedWithUIProps<TEvent>).isOverlapped = events.some(
			(e) => e !== event && eventsOverlap(e, event)
		);

		// Calcular la propiedad dayContainMode para cada evento
		const eventStart = event.start.toPlainDate();
		const eventEnd = event.end.toPlainDate();

		// Si el evento empieza y termina en el mismo día
		if (Temporal.PlainDate.compare(eventStart, eventEnd) === 0) {
			(event as ICalendarEventExtendedWithUIProps<TEvent>).dayContainMode = 'contain';
		} else if (Temporal.PlainDate.compare(eventStart, plainDate) === 0) {
			(event as ICalendarEventExtendedWithUIProps<TEvent>).dayContainMode = 'start';
		} else if (Temporal.PlainDate.compare(eventEnd, plainDate) === 0) {
			(event as ICalendarEventExtendedWithUIProps<TEvent>).dayContainMode = 'end';
		} else {
			(event as ICalendarEventExtendedWithUIProps<TEvent>).dayContainMode = 'overflow';
		}

		//
	});

	return events as ICalendarEventExtendedWithUIProps<TEvent>[];
}

export function calculatePositionAndHeight<TEvent extends ICalendarEventExtendedWithUIProps<ICalendarEventMinimumData>>(
	event: ICalendarEventExtendedWithUIProps<TEvent>,
	hourHeightInPixels: number,
	timeZone: ITimeZone
): { top: string; h: string; durationMinutes: number } {
	const localStart = event.start.withTimeZone(timeZone);
	const localEnd = event.end.withTimeZone(timeZone);

	if (event.dayContainMode === 'contain') {
		// // Calcula el top como el número total de minutos desde la medianoche, convertido a píxeles
		const startMinutes = localStart.since(localStart.withPlainTime('00:00'), { largestUnit: 'minutes' }).minutes;
		const top = startMinutes * (hourHeightInPixels / 60);

		// Calcula la altura como la diferencia en minutos entre el inicio y el fin del evento, convertido a píxeles
		const durationMinutes = localStart.until(localEnd, { largestUnit: 'minutes' }).minutes;
		const height = durationMinutes * (hourHeightInPixels / 60);

		return { durationMinutes, top: `${top}px`, h: `${height}px` };
	}

	if (event.dayContainMode === 'start') {
		// // Calcula el top como el número total de minutos desde la medianoche, convertido a píxeles
		const startMinutes = localStart.since(localStart.withPlainTime('00:00'), { largestUnit: 'minutes' }).minutes;
		const top = startMinutes * (hourHeightInPixels / 60);

		// Calcula la altura como la diferencia en minutos entre el inicio y el fin del evento, convertido a píxeles
		const durationMinutes = localStart.until(localStart.add({ days: 1 }).withPlainTime('00:00'), {
			largestUnit: 'minutes',
		}).minutes;
		const height = durationMinutes * (hourHeightInPixels / 60);

		return { durationMinutes, top: `${top}px`, h: `${height}px` };
	}

	if (event.dayContainMode === 'end') {
		// Calcula la altura como la diferencia en minutos entre el inicio y el fin del evento, convertido a píxeles
		const durationMinutes = localEnd.since(localEnd.withPlainTime('00:00'), { largestUnit: 'minutes' }).minutes;
		const height = durationMinutes * (hourHeightInPixels / 60);

		return { durationMinutes, top: `${0}px`, h: `${height}px` };
	}

	if (event.dayContainMode === 'overflow') {
		const top = 0;
		const height = 1440 * (hourHeightInPixels / 60);
		return { durationMinutes: 1440, top: `${top}px`, h: `${height}px` };
	}

	throw Error('Unknown day contain mode');
}

/**
 * Helper function to check if 2 events have some kind of overlap
 */
function eventsOverlap(event1: ICalendarEventMinimumData, event2: ICalendarEventMinimumData): boolean {
	return (
		Temporal.ZonedDateTime.compare(event1.start, event2.end) < 0 &&
		Temporal.ZonedDateTime.compare(event2.start, event1.end) < 0
	);
}

function getDateFromMouseEvent(
	e: React.PointerEvent<HTMLDivElement> | React.DragEvent,
	correction = 0,
	hourHeightInPixels = 60,
	plainDate: Temporal.PlainDate
): Temporal.PlainDateTime {
	const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
	const y = e.clientY - rect.top;

	return _getDateFromYOffset(y - correction, hourHeightInPixels, plainDate);
}

function _getDateFromYOffset(
	yOffset: number,
	hourHeightInPixels = 60,
	plainDate: Temporal.PlainDate
): Temporal.PlainDateTime {
	const hour = Math.floor(yOffset / hourHeightInPixels);
	const minute = Math.floor((yOffset % hourHeightInPixels) / (hourHeightInPixels / 60));
	const date = Temporal.PlainDate.from(plainDate);

	return date.toPlainDateTime().with({ hour, minute });
}

export default {
	getDateFromMouseEvent,
	assignZIndexAndLeft,
	calculatePositionAndHeight,
};
